import cx from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Card } from 'semantic-ui-react';

import { userAgenda } from '../../agenda/store/agenda.selectors';
import { workshopProptypes } from '../../propTypes';
import Images from '../../utils/Images';
import { checkUserAvailability } from '../../utils/agendaUtils';
import { getClassName } from '../../workshops/utils';
import EntityLink from '../EntityLink';
import SpeakerAvatars from '../speakers/SpeakerAvatars';
import './ClickableWorkshopCard.scss';
import QuotaField from './QuotaField';
import WorkshopDate from './WorkshopDate';
import { WorkshopLiveBadge } from './WorkshopLiveBadge';

function ClickableWorkshopCard(props) {
  const {
    workshop,
    minHeight,
    showDay,
    showDuration,
    showSpeakers,
    showEndTime,
    showIcons,
    showTimezone,
    dateConfig,
  } = props;
  const {
    _id,
    endDate,
    image,
    quota,
    shortDescription,
    speakers,
    startDate,
    title,
    usersCount,
    workshopDuration: duration,
    workshopStatus,
    isLive,
  } = workshop;

  const hasImage = image && image.uri;
  const agenda = useSelector(userAgenda);
  const hasOverlappingEvent = useMemo(() => {
    const otherEvents = agenda.filter((evt) => evt._id !== _id);
    return !checkUserAvailability(otherEvents, workshop);
  }, [agenda, workshop, _id]);

  return (
    <div
      className={cx(
        'workshop--clickable--card',
        getClassName(workshop),
        `session--${workshopStatus}`,
        hasOverlappingEvent && `session--has-overlapping-event`,
      )}
      style={{ minHeight }}
    >
      <Card as={EntityLink} entity={workshop}>
        {!hasImage && <WorkshopLiveBadge sessionStatus={isLive} onImage variant="clickable" />}
        {hasImage && (
          <div
            className="card__image"
            style={{
              backgroundImage: `url(${Images.cdn(image)})`,
            }}
          >
            <WorkshopLiveBadge sessionStatus={isLive} onImage variant="clickable" />
            {duration && <div className="card__duration">{duration}</div>}
          </div>
        )}
        <Card.Content
          className={cx('borderless', { 'has-live-badge-corner': isLive && !hasImage })}
        >
          {startDate && (
            <Card.Description className="card__date" style={{ marginTop: 8, marginBottom: 8 }}>
              <WorkshopDate
                startDate={startDate}
                endDate={endDate}
                showDay={showDay}
                showDuration={showDuration}
                showEndTime={showEndTime}
                showIcons={showIcons}
                showTimezone={showTimezone}
                dateConfig={dateConfig}
              />
            </Card.Description>
          )}
          <QuotaField quota={quota} usersCount={usersCount} />
          <Card.Header className="card__title" style={{ fontSize: '1.2em', fontWeight: 'normal' }}>
            {title}
          </Card.Header>
          {showSpeakers && <SpeakerAvatars speakerIds={speakers} />}
          {shortDescription && (
            <Card.Description className="card__shortDescription">
              {shortDescription}
            </Card.Description>
          )}
        </Card.Content>
      </Card>
    </div>
  );
}

ClickableWorkshopCard.defaultProps = {
  minHeight: undefined,
  showDay: undefined,
  showSpeakers: undefined,
  showDuration: undefined,
  showEndTime: undefined,
  showTimezone: undefined,
  dateConfig: undefined,
};

ClickableWorkshopCard.propTypes = {
  minHeight: PropTypes.number,
  workshop: PropTypes.shape(workshopProptypes).isRequired,
  showDay: PropTypes.bool,
  showSpeakers: PropTypes.bool,
  showDuration: PropTypes.bool,
  showEndTime: PropTypes.bool,
  showTimezone: PropTypes.bool,
  dateConfig: PropTypes.bool,
};

export default ClickableWorkshopCard;
